import { useMemo, useState } from "react";
import { Instagram } from "lucide-react";

/**
 * Renders a single Instagram reel/post as a responsive iframe embed.
 *
 * We use Instagram's `/embed` iframe endpoint (no script tag, no Meta API,
 * no OAuth) so it works for any public permalink the owner pastes. The
 * shortcode is parsed from the URL; if it can't be parsed we show a simple
 * "open on Instagram" fallback link instead of a broken frame.
 */

interface ParsedPermalink {
  // The URL path segment Instagram expects for the embed: "p" for posts,
  // "reel" for reels/IGTV. Derived from the pasted permalink itself so the
  // embed works regardless of how `mediaType` was set.
  kind: "p" | "reel";
  shortcode: string;
}

function parsePermalink(url: string): ParsedPermalink | null {
  try {
    const u = new URL(url);
    // Matches /p/<code>, /reel/<code>, /reels/<code>, /tv/<code>
    const m = u.pathname.match(/\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
    if (!m) return null;
    const kind = m[1] === "p" ? "p" : "reel";
    return { kind, shortcode: m[2] };
  } catch {
    return null;
  }
}

export interface InstagramEmbedProps {
  url: string;
  mediaType?: string;
  title?: string | null;
}

export function InstagramEmbed({ url, mediaType, title }: InstagramEmbedProps) {
  const [loaded, setLoaded] = useState(false);
  const parsed = useMemo(() => parsePermalink(url), [url]);

  // Reels are vertical (9:16); posts are roughly square but the embed chrome
  // (caption/header) needs extra height, so we give posts a taller box.
  const aspect = mediaType === "post" ? "3 / 4" : "9 / 16";

  if (!parsed) {
    return (
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 flex flex-col items-center justify-center gap-3 text-center">
        <Instagram className="w-6 h-6 text-primary/60" />
        {title && <p className="text-sm font-medium text-white/80">{title}</p>}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline break-all"
        >
          View on Instagram
        </a>
      </div>
    );
  }

  const embedSrc = `https://www.instagram.com/${parsed.kind}/${parsed.shortcode}/embed`;

  return (
    <div className="flex flex-col gap-2">
      {title && <p className="text-sm font-medium text-white/80 px-1">{title}</p>}
      <div
        className="relative w-full overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0a0a14]"
        style={{ aspectRatio: aspect }}
      >
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Instagram className="w-8 h-8 text-primary/30 animate-pulse" />
          </div>
        )}
        <iframe
          src={embedSrc}
          title={title ?? "Instagram video"}
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          scrolling="no"
          className="absolute inset-0 h-full w-full"
          style={{ border: "none" }}
          onLoad={() => setLoaded(true)}
        />
      </div>
    </div>
  );
}
